// Глобален банер със съобщение към клиентите — например проблем във връзката с
// КАТ, планиран престой при застраховател или промо кампания.
//
// СЪДЪРЖАНИЕТО НЕ Е ТУК. То живее в `static/content/site-notice.json` и се чете
// от сайта по време на работа (както новините/инвеститорите четат
// `/content/about-us.json`). Затова включване, изключване и смяна на текста
// стават само с редакция на този JSON на сървъра — БЕЗ нов билд.
//
// В този файл остават само:
//   1. адресът на JSON-а (`GLOBAL_BANNER_URL`),
//   2. предпазителите (`BANNER_GUARDS`) — кои конфигурации и кои страници изобщо
//      имат право да показват банер. Дори банер с `pages: ["*"]` няма да излезе
//      в процеса на покупка, профила или плащанията.
//   3. логиката за избор на банер спрямо текущата страница.
//
// Формат на банер в JSON-а:
//   id                  string   уникален ключ (ползва се и при затваряне)
//   enabled             bool     включва/изключва конкретното съобщение
//   tenants             array    за кои конфигурации важи; липсва = за всички
//   variant             string   info | warning | success | promo (цветова схема)
//   icon                string   bell | info | warning | gift | megaphone | none
//   dismissible         bool     показва бутон "затвори"
//   dismissPersistence  string   session (до затваряне на таба) | permanent (завинаги)
//   pages               array    "*" = всички страници, или списък с пътища;
//                                поддържа шаблон в края: "/proverka-*"
//   excludePages        array    изключения от `pages`
//   message             string | { bg, en, ro }
//   cta                 object   { url, label: string | { bg, en, ro }, external }

import { PAGE_LAST_MODIFIED } from "./pageLastModified";

const CONFIGURATION = process.env.GATSBY_CONFIGURATION || "boleron";

const DEFAULT_LANGUAGE = "bg";

const HOME_PAGE = "/";

const INFO_PAGES = ["/about-us/", "/contacts/", "/documents/", "/investors/"];

// Продуктовите и категорийните страници от първо ниво. Взимат се от
// `pageLastModified.js`, който е единственият списък с тези страници — така нова
// продуктова страница влиза автоматично, без да се дублира списък тук.
const PRODUCT_PAGES = Object.keys(PAGE_LAST_MODIFIED);

// Банерът е съобщение към всички клиенти: показва се на публичните страници, но
// не и в процеса на покупка, профила, плащанията и служебните страници.
const PUBLIC_PAGES = [HOME_PAGE, ...INFO_PAGES, ...PRODUCT_PAGES];

// Адрес на редактируемото съдържание. Файлът е в `static/`, затова се сервира
// както си е от корена на сайта, а `.htaccess` не го кешира
// (`ExpiresByType application/json "access plus 0 seconds"`) — промяна на сървъра
// се вижда при следващото зареждане на страница.
//
// ВНИМАНИЕ: името на файла умишлено НЕ съдържа "banner"/"ad"/"promo". Блокерите
// на реклами режат такива адреси (EasyList) и съобщението нямаше да стигне до
// част от клиентите. Не преименувай файла на нещо от този тип.
export const GLOBAL_BANNER_URL = "/content/site-notice.json";

// Предпазителите не могат да се променят от JSON-а — те са кодът, който пази
// банера далеч от процеса на покупка.
export const BANNER_GUARDS = {
  allowedTenants: ["boleron"],
  allowedPages: PUBLIC_PAGES
};

const EMPTY_CONFIG = { showGlobalBanner: false, banners: [] };

/**
 * Слепва прочетеното от JSON-а съдържание с предпазителите от кода.
 */
export const withGuards = content => ({ ...EMPTY_CONFIG, ...(typeof content === "object" && content !== null ? content : {}), ...BANNER_GUARDS });

const stripLanguagePrefix = path => (path || "/").replace(/^\/[a-z]{2}(?=\/|$)/, "") || "/";

const normalizeBannerPath = path => {
  const withoutLanguage = stripLanguagePrefix(path);
  return withoutLanguage.endsWith("/") ? withoutLanguage : `${withoutLanguage}/`;
};

const matchesPage = (pattern, normalizedPath) => {
  if (!pattern) return false;

  const cleanedPattern = String(pattern).trim();
  if (cleanedPattern === "*") return true;

  if (cleanedPattern.endsWith("*")) {
    return normalizedPath.startsWith(stripLanguagePrefix(cleanedPattern.slice(0, -1)));
  }

  return normalizeBannerPath(cleanedPattern) === normalizedPath;
};

const isForCurrentTenant = banner => !banner?.tenants?.length || banner.tenants.includes(CONFIGURATION);

// Общите предпазители липсват ли — не ограничаваме нищо (важи за тестове и за
// конфигурации, които разчитат само на таргетирането на отделните банери).
const isAllowedTenant = config => !config?.allowedTenants?.length || config.allowedTenants.includes(CONFIGURATION);

const isAllowedPage = (config, normalizedPath) => !config?.allowedPages?.length || config.allowedPages.some(page => matchesPage(page, normalizedPath));

/**
 * Взима езиковия вариант на текст от конфигурацията.
 * Приема както обикновен string (важи за всички езици), така и { bg, en, ro }.
 */
export const getBannerText = (value, language) => {
  if (!value) return "";
  if (typeof value === "string") return value;

  return value[language] || value[DEFAULT_LANGUAGE] || Object.values(value).find(Boolean) || "";
};

/**
 * Връща банера, който трябва да се покаже на дадения път, или null.
 * `config` е съдържанието от JSON-а, минато през `withGuards`.
 * Пътят може да е с езиков префикс (/en/contacts/) — префиксът се маха.
 */
export const getGlobalBanner = (path, config = EMPTY_CONFIG) => {
  if (!config?.showGlobalBanner || !isAllowedTenant(config)) return null;

  const normalizedPath = normalizeBannerPath(path);
  if (!isAllowedPage(config, normalizedPath)) return null;

  return (
    config.banners?.find(
      banner =>
        banner?.id &&
        banner.enabled !== false &&
        isForCurrentTenant(banner) &&
        (banner.pages?.length ? banner.pages : ["*"]).some(page => matchesPage(page, normalizedPath)) &&
        !banner.excludePages?.some(page => matchesPage(page, normalizedPath))
    ) || null
  );
};
